import { useMemo } from "react";
import { useMsg } from "../../../components/Msg/Msg";
import {
  CERTIFICATE,
  DELIVERY_FORMAT,
  DIAGNOSTIC_TOOL,
  INDUSTRY_EXPERIENCE,
  PRIMARY_ROLE,
  SERVICE_TYPE,
  TRAVEL_WILLINGNESS,
} from "../constants";
import { settingsMessages } from "../messages";
import { settingsOptionsMessages } from "./messages";
import { generalMessages } from "../../../components/messages";

export const useCertificateOptions = () => {
  const generalMsg = useMsg({ dict: generalMessages });

  return useMemo(
    () => [
      { value: CERTIFICATE.ACC, label: "ICF ACC / EMCC EIA Practitioner" },
      {
        value: CERTIFICATE.PCC,
        label: "ICF PCC / EMCC EIA Senior Practitioner",
      },
      {
        value: CERTIFICATE.MCC,
        label: "ICF MCC / EMCC EIA Master Practitioner",
      },
      { value: CERTIFICATE.MBTI, label: "MBTI" },
      {
        value: CERTIFICATE.GALLUP_STRENGTHSFINDER,
        label: "Gallup StrengthsFinder",
      },
      { value: CERTIFICATE.DISC, label: "DISC" },
      { value: CERTIFICATE.HOGAN, label: "Hogan" },
      { value: CERTIFICATE.LPI, label: "LPI" },
      { value: CERTIFICATE.FEEDBACK_360, label: "360 Feedback" },
      { value: CERTIFICATE.SHL, label: "SHL" },
      { value: CERTIFICATE.INSIGHTS_DISCOVERY, label: "Insights Discovery" },
      { value: CERTIFICATE.OTHER, label: generalMsg("general.other") },
    ],
    [generalMsg],
  );
};

export const useTravelWillingnessOptions = () => {
  const msg = useMsg({ dict: settingsOptionsMessages });
  return useMemo(
    () => [
      {
        value: TRAVEL_WILLINGNESS.NO,
        label: msg("dict.settings.options.travelWillingness.NO"),
      },
      {
        value: TRAVEL_WILLINGNESS.WITHIN_CITY,
        label: msg("dict.settings.options.travelWillingness.WITHIN_CITY"),
      },
      {
        value: TRAVEL_WILLINGNESS.WITHIN_COUNTRY,
        label: msg("dict.settings.options.travelWillingness.WITHIN_COUNTRY"),
      },
      {
        value: TRAVEL_WILLINGNESS.WITHIN_REGION,
        label: msg("dict.settings.options.travelWillingness.WITHIN_REGION"),
      },
      {
        value: TRAVEL_WILLINGNESS.WITHIN_EUROPE,
        label: msg("dict.settings.options.travelWillingness.WITHIN_EUROPE"),
      },
      {
        value: TRAVEL_WILLINGNESS.GLOBALLY,
        label: msg("dict.settings.options.travelWillingness.GLOBALLY"),
      },
      {
        value: TRAVEL_WILLINGNESS.OTHER,
        label: msg("dict.settings.options.travelWillingness.OTHER"),
      },
    ],
    [msg],
  );
};

export const useDeliveryFormatOptions = () => {
  const msg = useMsg({ dict: settingsOptionsMessages });
  return useMemo(
    () => [
      {
        value: DELIVERY_FORMAT.ONLINE,
        label: msg("dict.settings.options.deliveryFormat.ONLINE"),
      },
      {
        value: DELIVERY_FORMAT.ONSITE,
        label: msg("dict.settings.options.deliveryFormat.ONSITE"),
      },
      {
        value: DELIVERY_FORMAT.HYBRID,
        label: msg("dict.settings.options.deliveryFormat.HYBRID"),
      },
      {
        value: DELIVERY_FORMAT.OTHER,
        label: msg("dict.settings.options.deliveryFormat.OTHER"),
      },
    ],
    [msg],
  );
};

export const useServiceTypeOptions = () => {
  const msg = useMsg({ dict: settingsOptionsMessages });
  return useMemo(
    () => [
      {
        value: SERVICE_TYPE.ONE_TO_ONE,
        label: msg("dict.settings.options.serviceType.ONE_TO_ONE"),
      },
      {
        value: SERVICE_TYPE.EXECUTIVE,
        label: msg("dict.settings.options.serviceType.EXECUTIVE"),
      },
      {
        value: SERVICE_TYPE.TEAM,
        label: msg("dict.settings.options.serviceType.TEAM"),
      },
      {
        value: SERVICE_TYPE.CAREER,
        label: msg("dict.settings.options.serviceType.CAREER"),
      },
      {
        value: SERVICE_TYPE.GROUP,
        label: msg("dict.settings.options.serviceType.GROUP"),
      },
      {
        value: SERVICE_TYPE.PEER_MENTORING,
        label: msg("dict.settings.options.serviceType.PEER_MENTORING"),
      },
      {
        value: SERVICE_TYPE.WORKSHOPS,
        label: msg("dict.settings.options.serviceType.WORKSHOPS"),
      },
      {
        value: SERVICE_TYPE.LONGER_PROGRAMS,
        label: msg("dict.settings.options.serviceType.LONGER_PROGRAMS"),
      },
      {
        value: SERVICE_TYPE.OTHER,
        label: msg("dict.settings.options.serviceType.OTHER"),
      },
    ],
    [msg],
  );
};

export const useDiagnosticToolOptions = () => {
  const msg = useMsg({ dict: settingsOptionsMessages });
  return useMemo(
    () => [
      {
        value: DIAGNOSTIC_TOOL.MBTI,
        label: msg("dict.settings.options.diagnosticTool.MBTI"),
      },
      {
        value: DIAGNOSTIC_TOOL.GALLUP_STRENGTHSFINDER,
        label: msg(
          "dict.settings.options.diagnosticTool.GALLUP_STRENGTHSFINDER",
        ),
      },
      {
        value: DIAGNOSTIC_TOOL.DISC,
        label: msg("dict.settings.options.diagnosticTool.DISC"),
      },
      {
        value: DIAGNOSTIC_TOOL.HOGAN,
        label: msg("dict.settings.options.diagnosticTool.HOGAN"),
      },
      {
        value: DIAGNOSTIC_TOOL.LPI,
        label: msg("dict.settings.options.diagnosticTool.LPI"),
      },
      {
        value: DIAGNOSTIC_TOOL.FEEDBACK_360,
        label: msg("dict.settings.options.diagnosticTool.FEEDBACK_360"),
      },
      {
        value: DIAGNOSTIC_TOOL.SHL,
        label: msg("dict.settings.options.diagnosticTool.SHL"),
      },
      {
        value: DIAGNOSTIC_TOOL.INSIGHTS_DISCOVERY,
        label: msg("dict.settings.options.diagnosticTool.INSIGHTS_DISCOVERY"),
      },
      {
        value: DIAGNOSTIC_TOOL.OTHER,
        label: msg("dict.settings.options.diagnosticTool.OTHER"),
      },
    ],
    [msg],
  );
};

export const useIndustryExperienceOptions = () => {
  const msg = useMsg({ dict: settingsOptionsMessages });
  return useMemo(
    () => [
      {
        value: INDUSTRY_EXPERIENCE.TECH,
        label: msg("dict.settings.options.industryExperience.TECH"),
      },
      {
        value: INDUSTRY_EXPERIENCE.FINANCE,
        label: msg("dict.settings.options.industryExperience.FINANCE"),
      },
      {
        value: INDUSTRY_EXPERIENCE.PHARMA,
        label: msg("dict.settings.options.industryExperience.PHARMA"),
      },
      {
        value: INDUSTRY_EXPERIENCE.RETAIL,
        label: msg("dict.settings.options.industryExperience.RETAIL"),
      },
      {
        value: INDUSTRY_EXPERIENCE.MANUFACTURING,
        label: msg("dict.settings.options.industryExperience.MANUFACTURING"),
      },
      {
        value: INDUSTRY_EXPERIENCE.LOGISTICS,
        label: msg("dict.settings.options.industryExperience.LOGISTICS"),
      },
      {
        value: INDUSTRY_EXPERIENCE.TELECOMMUNICATIONS,
        label: msg(
          "dict.settings.options.industryExperience.TELECOMMUNICATIONS",
        ),
      },
      {
        value: INDUSTRY_EXPERIENCE.PUBLIC_SECTOR,
        label: msg("dict.settings.options.industryExperience.PUBLIC_SECTOR"),
      },
      {
        value: INDUSTRY_EXPERIENCE.GENERAL_CROSS_INDUSTRY,
        label: msg(
          "dict.settings.options.industryExperience.GENERAL_CROSS_INDUSTRY",
        ),
      },
      {
        value: INDUSTRY_EXPERIENCE.OTHER,
        label: msg("dict.settings.options.industryExperience.OTHER"),
      },
    ],
    [msg],
  );
};

export const usePrimaryRolesOptions = () => {
  const msg = useMsg({ dict: settingsMessages });
  return useMemo(
    () => [
      {
        value: PRIMARY_ROLE.COACH,
        label: msg("settings.profile.field.primaryRoles.option.coach"),
      },
      {
        value: PRIMARY_ROLE.MENTOR,
        label: msg("settings.profile.field.primaryRoles.option.mentor"),
      },
      {
        value: PRIMARY_ROLE.TRAINER,
        label: msg("settings.profile.field.primaryRoles.option.trainer"),
      },
      {
        value: PRIMARY_ROLE.FACILITATOR,
        label: msg("settings.profile.field.primaryRoles.option.facilitator"),
      },
      {
        value: PRIMARY_ROLE.CONSULTANT,
        label: msg("settings.profile.field.primaryRoles.option.consultant"),
      },
      {
        value: PRIMARY_ROLE.SPEAKER,
        label: msg("settings.profile.field.primaryRoles.option.speaker"),
      },
    ],
    [msg],
  );
};
